import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";

interface FishSpecies {
  id: number;
  common_name: string;
  scientific_name: string;
  category: string;
  rarity: string;
  difficulty_tier: number;
}

interface FishSpeciesComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  required?: boolean;
  id?: string;
}

export function FishSpeciesCombobox({ 
  value, 
  onValueChange, 
  required = false,
  id 
}: FishSpeciesComboboxProps) {
  const [species, setSpecies] = useState<FishSpecies[]>([]);
  const [filteredSpecies, setFilteredSpecies] = useState<FishSpecies[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchSpecies();
  }, []);

  useEffect(() => {
    if (value) {
      const filtered = species.filter((fish) =>
        fish.common_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSpecies(filtered);
    } else {
      setFilteredSpecies([]);
    }
  }, [value, species]);

  const fetchSpecies = async () => {
    try {
      const { data, error } = await supabase
        .from("fish_species")
        .select("id, common_name, scientific_name, category, rarity, difficulty_tier")
        .order("common_name");

      if (error) throw error;
      setSpecies(data || []);
    } catch (error) {
      console.error("Error fetching fish species:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case "rare":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "uncommon":
        return "bg-primary/10 text-primary border-primary/20";
      case "abundant":
      case "common":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        required={required}
        placeholder="Type to search species..."
        className="w-full"
      />
      
      {showSuggestions && filteredSpecies.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 max-h-[300px] overflow-auto bg-background border border-border rounded-md shadow-lg"
        >
          {filteredSpecies.map((fish) => (
            <div
              key={fish.id}
              onClick={() => {
                onValueChange(fish.common_name);
                setShowSuggestions(false);
              }}
              className="flex items-center gap-2 p-3 cursor-pointer hover:bg-accent transition-colors border-b border-border last:border-b-0"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">
                    {fish.common_name}
                  </span>
                  <Badge 
                    variant="outline" 
                    className={cn("text-xs shrink-0", getRarityColor(fish.rarity))}
                  >
                    {fish.rarity}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {fish.scientific_name} • {fish.category} • Tier {fish.difficulty_tier}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
